import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { isWebAuthnSupported, startRegistration } from '../utils/webauthn';

const MFASetupPage = () => {
  const navigate = useNavigate();

  // Enrollment context from login redirect
  const [enrollmentToken] = useState(() => sessionStorage.getItem('mfa_enrollment_token'));
  const [enrollmentMessage] = useState(() => sessionStorage.getItem('mfa_enrollment_message'));
  const [mfaPolicy] = useState(() => sessionStorage.getItem('mfa_enrollment_policy') || 'any');

  // Shared state
  const [method, setMethod] = useState(null); // null = choosing, 'totp', 'webauthn'
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [codesCopied, setCodesCopied] = useState(false);

  // TOTP state
  const [qrCode, setQrCode] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  // WebAuthn state
  const [deviceName, setDeviceName] = useState('');
  const [webauthnBusy, setWebauthnBusy] = useState(false);

  const webauthnSupported = isWebAuthnSupported();

  // Auto-select method based on policy
  useEffect(() => {
    if (mfaPolicy === 'webauthn_only') {
      setMethod('webauthn');
    }
  }, [mfaPolicy]);

  const finishSetup = () => {
    sessionStorage.removeItem('mfa_enrollment_token');
    sessionStorage.removeItem('mfa_enrollment_message');
    sessionStorage.removeItem('mfa_enrollment_policy');
    navigate('/login', { state: { message: 'MFA setup completed successfully' } });
  };

  // Initialize TOTP setup when method is selected
  useEffect(() => {
    if (method !== 'totp') return;
    let cancelled = false;
    const initTotp = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await authAPI.setupMFA(enrollmentToken);
        if (cancelled) return;
        if (response.status === 'success') {
          setQrCode(response.qrcode);
          setSetupToken(response.setup_token);
        } else {
          setError(response.error || 'Failed to initialize TOTP setup');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || err.error || 'Failed to initialize TOTP setup');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    initTotp();
    return () => { cancelled = true; };
  }, [method, enrollmentToken]);

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await authAPI.completeMFASetup({
        setup_token: setupToken,
        mfa_code: totpCode
      }, enrollmentToken);
      if (response.status === 'success') {
        setRecoveryCodes(response.recovery_codes || null);
        if (!response.recovery_codes) finishSetup();
      } else {
        setError(response.error || 'Failed to verify MFA code');
      }
    } catch (err) {
      setError(err.message || err.error || 'Failed to verify MFA code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWebAuthnRegister = async () => {
    if (!deviceName.trim()) {
      setError('Please enter a name for your security key');
      return;
    }
    setError('');
    setWebauthnBusy(true);
    try {
      const options = await authAPI.getWebAuthnRegisterOptions(enrollmentToken);
      const credential = await startRegistration(options);
      const result = await authAPI.verifyWebAuthnRegistration(credential, deviceName.trim(), enrollmentToken);
      if (result.recovery_codes) {
        setRecoveryCodes(result.recovery_codes);
      } else {
        finishSetup();
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Security key operation was cancelled or timed out.');
      } else if (err.name === 'SecurityError') {
        setError('Security key registration failed. Make sure you are on the correct domain.');
      } else {
        setError(err.message || err.error || 'Security key registration failed');
      }
    } finally {
      setWebauthnBusy(false);
    }
  };

  const handleTotpCodeChange = (e) => {
    setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
  };

  // ── Recovery codes screen (shared by both methods) ──
  if (recoveryCodes) {
    const handleCopy = () => {
      navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCodesCopied(true);
      setTimeout(() => setCodesCopied(false), 2000);
    };

    const handleDownload = () => {
      const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mfa-recovery-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Save Your Recovery Codes
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Store these safely. Each code can be used once to access your account if you lose your authenticator device.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  These codes will only be shown once. You won't be able to see them again.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {recoveryCodes.map((c, i) => (
                <div key={i} className="text-gray-800 text-center py-1 bg-white rounded border border-gray-100">
                  {c}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCopy}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {codesCopied ? 'Copied!' : 'Copy Codes'}
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Download
            </button>
          </div>

          <button
            onClick={finishSetup}
            className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-hover"
          >
            I've Saved My Codes — Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Loading spinner ──
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ── Method selector (when policy is 'any' and user hasn't chosen yet) ──
  if (!method) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Set Up MFA
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {enrollmentMessage || 'MFA enrollment is required before you can access this application.'}
            </p>
            <p className="mt-1 text-center text-sm text-gray-500">
              Choose a method to secure your account.
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => setMethod('totp')}
              className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
            >
              <div className="flex-shrink-0">
                <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-900">Authenticator App</div>
                <div className="text-sm text-gray-500">Use Google Authenticator, Authy, or similar</div>
              </div>
            </button>

            <button
              onClick={() => webauthnSupported && setMethod('webauthn')}
              disabled={!webauthnSupported}
              className={`w-full flex items-center gap-4 p-4 border-2 rounded-lg text-left transition-colors ${
                webauthnSupported
                  ? 'border-gray-200 hover:border-primary hover:bg-primary/5'
                  : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
              }`}
            >
              <div className="flex-shrink-0">
                <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-900">Security Key</div>
                <div className="text-sm text-gray-500">
                  {webauthnSupported
                    ? 'Use a YubiKey or other FIDO2 security key'
                    : 'Your browser does not support security keys'}
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── WebAuthn registration flow ──
  if (method === 'webauthn') {
    // Browser doesn't support WebAuthn — show error (only reachable for webauthn_only policy)
    if (!webauthnSupported) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                Security Key Required
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                {enrollmentMessage || 'Your organisation requires a security key for authentication.'}
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-700">
                Your browser does not support security keys (WebAuthn). Please use a supported browser such as Chrome, Edge, Firefox, or Safari.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Register Security Key
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {enrollmentMessage || 'Insert your security key and give it a name to get started.'}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="device-name" className="block text-sm font-medium text-gray-700 mb-1">
                Key Name
              </label>
              <input
                id="device-name"
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder='e.g. "YubiKey 5 NFC"'
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                maxLength={255}
                disabled={webauthnBusy}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center">
                {error}
              </div>
            )}

            <button
              onClick={handleWebAuthnRegister}
              disabled={webauthnBusy || !deviceName.trim()}
              className={`w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                !webauthnBusy && deviceName.trim()
                  ? 'bg-primary hover:bg-primary-hover'
                  : 'bg-indigo-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
            >
              {webauthnBusy ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Waiting for security key...
                </span>
              ) : (
                'Register Security Key'
              )}
            </button>

            {mfaPolicy !== 'webauthn_only' && (
              <button
                onClick={() => { setMethod(null); setError(''); setDeviceName(''); }}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
                disabled={webauthnBusy}
              >
                Use a different method
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── TOTP setup flow (existing behaviour) ──
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Set Up MFA
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {enrollmentMessage
              ? `${enrollmentMessage} Scan the QR code with your authenticator app and enter the code to enable MFA.`
              : 'Scan the QR code with your authenticator app and enter the code to enable MFA.'}
          </p>
        </div>

        {qrCode && (
          <div className="flex justify-center">
            <img
              src={`data:image/png;base64,${qrCode}`}
              alt="QR Code"
              className="w-64 h-64"
            />
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleTotpSubmit}>
          <div>
            <label htmlFor="mfa-code" className="sr-only">
              MFA Code
            </label>
            <input
              id="mfa-code"
              name="code"
              type="text"
              pattern="[0-9]*"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm tracking-widest text-center letter-spacing-2"
              placeholder="Enter 6-digit code"
              value={totpCode}
              onChange={handleTotpCodeChange}
              maxLength={6}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={totpCode.length !== 6 || isLoading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                totpCode.length === 6 && !isLoading
                  ? 'bg-primary hover:bg-primary-hover'
                  : 'bg-indigo-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Complete Setup'
              )}
            </button>
          </div>
        </form>

        {mfaPolicy !== 'webauthn_only' && (
          <button
            onClick={() => { setMethod(null); setError(''); setTotpCode(''); setQrCode(''); setSetupToken(''); }}
            className="w-full text-sm text-gray-500 hover:text-gray-700"
          >
            Use a different method
          </button>
        )}
      </div>
    </div>
  );
};

export default MFASetupPage;
